import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as hostelService from "../service/hostelService";
import type { HostelState } from "../types";
import i18n from "@/i18n";

export const fetchBuildings = createAsyncThunk("hostel/fetchBuildings", async (institutionId: number) => {
  return await hostelService.fetchBuildings(institutionId);
});

export const createBuilding = createAsyncThunk(
  "hostel/createBuilding",
  async ({ institutionId, payload }: { institutionId: number; payload: Parameters<typeof hostelService.createBuilding>[1] }, { dispatch }) => {
    await hostelService.createBuilding(institutionId, payload);
    await dispatch(fetchBuildings(institutionId));
  }
);

export const fetchRooms = createAsyncThunk(
  "hostel/fetchRooms",
  async ({ institutionId, buildingId }: { institutionId: number; buildingId?: number }) => {
    return await hostelService.fetchRooms(institutionId, buildingId);
  }
);

export const createRoom = createAsyncThunk(
  "hostel/createRoom",
  async (
    { institutionId, payload }: { institutionId: number; payload: Parameters<typeof hostelService.createRoom>[1] },
    { dispatch }
  ) => {
    await hostelService.createRoom(institutionId, payload);
    await dispatch(fetchRooms({ institutionId }));
  }
);

export const allocate = createAsyncThunk(
  "hostel/allocate",
  async (
    { institutionId, payload }: { institutionId: number; payload: Parameters<typeof hostelService.allocate>[1] },
    { dispatch }
  ) => {
    await hostelService.allocate(institutionId, payload);
    await dispatch(fetchRooms({ institutionId }));
  }
);

export const vacate = createAsyncThunk(
  "hostel/vacate",
  async (
    { institutionId, allocationId, studentId, academicYearId }: { institutionId: number; allocationId: number; studentId: number; academicYearId?: number },
    { dispatch }
  ) => {
    await hostelService.vacate(institutionId, allocationId);
    await dispatch(fetchRooms({ institutionId }));
    await dispatch(fetchStudentAllocations({ institutionId, studentId, academicYearId }));
  }
);

export const updateBuilding = createAsyncThunk(
  "hostel/updateBuilding",
  async (
    { institutionId, buildingId, payload }: { institutionId: number; buildingId: number; payload: Parameters<typeof hostelService.updateBuilding>[2] },
    { dispatch }
  ) => {
    await hostelService.updateBuilding(institutionId, buildingId, payload);
    await dispatch(fetchBuildings(institutionId));
  }
);

export const deleteBuilding = createAsyncThunk(
  "hostel/deleteBuilding",
  async ({ institutionId, buildingId }: { institutionId: number; buildingId: number }, { dispatch }) => {
    await hostelService.deleteBuilding(institutionId, buildingId);
    await dispatch(fetchBuildings(institutionId));
    await dispatch(fetchStaff(institutionId));
  }
);

export const updateRoom = createAsyncThunk(
  "hostel/updateRoom",
  async (
    { institutionId, roomId, payload }: { institutionId: number; roomId: number; payload: Parameters<typeof hostelService.updateRoom>[2] },
    { dispatch }
  ) => {
    await hostelService.updateRoom(institutionId, roomId, payload);
    await dispatch(fetchRooms({ institutionId }));
  }
);

export const deleteRoom = createAsyncThunk(
  "hostel/deleteRoom",
  async ({ institutionId, roomId }: { institutionId: number; roomId: number }, { dispatch }) => {
    await hostelService.deleteRoom(institutionId, roomId);
    await dispatch(fetchRooms({ institutionId }));
  }
);

export const transfer = createAsyncThunk(
  "hostel/transfer",
  async (
    { institutionId, allocationId, studentId, academicYearId, payload }: {
      institutionId: number; allocationId: number; studentId: number; academicYearId?: number; payload: { roomId: number; bedId?: number };
    },
    { dispatch }
  ) => {
    await hostelService.transfer(institutionId, allocationId, payload);
    await dispatch(fetchRooms({ institutionId }));
    await dispatch(fetchStudentAllocations({ institutionId, studentId, academicYearId }));
  }
);

export const fetchStaff = createAsyncThunk("hostel/fetchStaff", async (institutionId: number) => {
  return await hostelService.fetchStaff(institutionId);
});

export const assignStaff = createAsyncThunk(
  "hostel/assignStaff",
  async ({ institutionId, payload }: { institutionId: number; payload: Parameters<typeof hostelService.assignStaff>[1] }, { dispatch }) => {
    await hostelService.assignStaff(institutionId, payload);
    await dispatch(fetchStaff(institutionId));
  }
);

export const endStaffAssignment = createAsyncThunk(
  "hostel/endStaffAssignment",
  async ({ institutionId, assignmentId }: { institutionId: number; assignmentId: number }, { dispatch }) => {
    await hostelService.endStaffAssignment(institutionId, assignmentId);
    await dispatch(fetchStaff(institutionId));
  }
);

export const fetchSettings = createAsyncThunk("hostel/fetchSettings", async (institutionId: number) => {
  return await hostelService.fetchSettings(institutionId);
});

export const updateSettings = createAsyncThunk(
  "hostel/updateSettings",
  async ({ institutionId, payload }: { institutionId: number; payload: { girlsRequireFemaleWarden: boolean } }) => {
    return await hostelService.updateSettings(institutionId, payload);
  }
);

export const fetchStudentAllocations = createAsyncThunk(
  "hostel/fetchStudentAllocations",
  async ({ institutionId, studentId, academicYearId }: { institutionId: number; studentId: number; academicYearId?: number }) => {
    return await hostelService.fetchStudentAllocations(institutionId, studentId, academicYearId);
  }
);

const initialState: HostelState = {
  buildings: [],
  rooms: [],
  studentAllocations: [],
  staff: [],
  settings: { girlsRequireFemaleWarden: false },
  loading: false,
  saving: false,
  error: null,
};

const hostelSlice = createSlice({
  name: "hostel",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuildings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuildings.fulfilled, (state, action) => {
        state.loading = false;
        state.buildings = action.payload;
      })
      .addCase(fetchBuildings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToLoadBuildings");
      })
      .addCase(createBuilding.pending, (state) => {
        state.error = null;
      })
      .addCase(createBuilding.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddBuilding");
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.rooms = action.payload;
      })
      .addCase(createRoom.pending, (state) => {
        state.error = null;
      })
      .addCase(createRoom.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddRoom");
      })
      .addCase(allocate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(allocate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(allocate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAllocateStudent");
      })
      .addCase(vacate.pending, (state) => {
        state.error = null;
      })
      .addCase(vacate.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToVacateAllocation");
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.staff = action.payload;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(fetchStudentAllocations.fulfilled, (state, action) => {
        state.studentAllocations = action.payload;
      })
      .addMatcher(
        (action) => /^hostel\/(updateBuilding|deleteBuilding|updateRoom|deleteRoom|transfer|assignStaff|endStaffAssignment|updateSettings)\/pending$/.test(action.type),
        (state) => {
          state.error = null;
        }
      )
      .addMatcher(
        (action) => /^hostel\/(updateBuilding|deleteBuilding|updateRoom|deleteRoom|transfer|assignStaff|endStaffAssignment|updateSettings)\/rejected$/.test(action.type),
        (state, action: { error?: { message?: string } }) => {
          state.error = action.error?.message ?? "Request failed";
        }
      );
  },
});

export default hostelSlice.reducer;
